// src/components/ws/wsCommon.js
// 出席記録（全画面）で使い回す小物【2026-10-13】
//
//   日付・時刻の見せ方、出席率の色、出欠の書き方（記号／文字）と回の見出し（日付／第N回）の
//   覚え方、要注意の学生の判定をまとめる。
//   出欠の書き方・見出しは、書き出し（ReportExportPane）と同じ localStorage のキーを使うので、
//   出欠表タブで切り替えたものがそのまま書き出しにも効く。

export const ATTENDED = ['present', 'late', 'early_leave'];

/** 時刻を 'HH:MM'（端末の時刻）にする。読めなければ空 */
export const fmtClock = (v) => {
  if (!v) return '';
  const x = new Date(v);
  if (isNaN(x.getTime())) return '';
  return String(x.getHours()).padStart(2, '0') + ':' + String(x.getMinutes()).padStart(2, '0');
};

/**
 * 授業日を 'YYYY-MM-DD'（端末の日付）にする。
 *   サーバーは DATE を '2026-09-27T15:00:00.000Z'（= JST の 9/28）で返すので、先頭10文字を切ると1日前になる。
 */
export const fmtDate = (d) => {
  if (!d) return '-';
  const str = String(d);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const x = new Date(str);
  if (isNaN(x.getTime())) return str.slice(0, 10);
  const p = (n) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`;
};

/** 'M/D' */
export const fmtMD = (d) => {
  const s = fmtDate(d).split('-');
  return s.length === 3 ? `${Number(s[1])}/${Number(s[2])}` : '';
};

const WEEK = ['日', '月', '火', '水', '木', '金', '土'];
/** '（木）' */
export const fmtWeekday = (d) => {
  const s = fmtDate(d);
  const x = new Date(s + 'T00:00:00');
  return isNaN(x.getTime()) ? '' : '（' + WEEK[x.getDay()] + '）';
};

/** 出席率の色。色だけに頼らないよう、数字は必ず一緒に出すこと */
export function rateColor(rate) {
  if (rate == null) return '#9ca3af';
  if (rate >= 80) return '#2563eb';
  if (rate >= 60) return '#d97706';
  return '#b91c1c';
}

// ---- 表示の設定（このブラウザに覚える） ------------------------------------
// キーは ReportExportPane と同じ（書き出しにも効かせるため）
export const MARKS_KEY   = 'reportMarks';
export const HEADING_KEY = 'reportHeading';
export const FONT_KEY    = 'reportPreviewFontStep';
export const SHEETS_KEY  = 'wsExportSheets';
export const FONT_STEPS  = [0.78, 0.85, 0.92, 1.0, 1.1, 1.25];
export const FONT_LABELS = ['極小', '小', '標準', '中', '大', '特大'];

export function readPref(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : v;
  } catch (e) { return fallback; }
}
export function writePref(key, value) {
  try { localStorage.setItem(key, String(value)); } catch (e) { /* 覚えられなくても動く */ }
}
export const loadMarks   = () => (readPref(MARKS_KEY, 'text') === 'symbol' ? 'symbol' : 'text');
export const loadHeading = () => (readPref(HEADING_KEY, 'date') === 'number' ? 'number' : 'date');
export const loadFontStep = () => {
  const v = parseInt(readPref(FONT_KEY, '2'), 10);
  return v >= 0 && v < FONT_STEPS.length ? v : 2;
};

// 出欠の書き方。記号は書き出し（出席簿の ◯△▲◎×）と同じ
export const STATUS_INFO = {
  present:     { symbol: '◯', text: '出席', short: '出', color: '#15803d', label: '出席' },
  late:        { symbol: '△', text: '遅刻', short: '遅', color: '#b45309', label: '遅刻' },
  early_leave: { symbol: '▲', text: '早退', short: '早', color: '#0e7490', label: '早退' },
  excused:     { symbol: '◎', text: '公欠', short: '公', color: '#6d28d9', label: '公欠' },
  irregular:   { symbol: '?', text: '確認', short: '?', color: '#b45309', label: '確認待ち' },
  absent:      { symbol: '×', text: '欠席', short: '欠', color: '#b91c1c', label: '欠席' },
};
export const statusInfo = (st) => STATUS_INFO[st] || STATUS_INFO.absent;

/**
 * 要注意の学生（書き出しの「要注意の学生」シートと同じ決め方）
 *   ・実施4回以上で出席率70%未満
 *   ・累計3回以上欠席
 *   ・直近2回以上続けて欠席（公欠は飛ばして数える）
 * @returns {Map<number, {streak:number, absent:number, reasons:string[]}>}
 */
export function riskOf(matrix) {
  const out = new Map();
  if (!matrix) return out;
  const sessions = matrix.sessions || [];
  const newestFirst = [...sessions].reverse();
  const all = matrix.students || [];
  const hasRoster = all.some(s => !s.is_outside_roster);
  const enrolled = hasRoster ? all.filter(s => !s.is_outside_roster) : all;
  for (const st of enrolled) {
    let streak = 0;
    for (const s of newestFirst) {
      const c = (st.cells || {})[s.id];
      if (ATTENDED.includes(c)) break;
      if (c === 'excused') continue;
      streak++;
    }
    const absent = Number(st.absent != null ? st.absent : 0);
    const reasons = [];
    if (streak >= 2) reasons.push(streak + '回連続で欠席');
    if (absent >= 3) reasons.push('累計' + absent + '回欠席');
    if (Number(st.held) >= 4 && st.rate != null && Number(st.rate) < 70) reasons.push('出席率' + st.rate + '%');
    if (reasons.length) out.set(st.id, { streak, absent, reasons });
  }
  return out;
}

/** 回を古い順に並べる（日付 → 開始時刻 → id）。サーバーの並び順に頼らない */
export function byDateAsc(a, b) {
  const da = a.session_ymd || fmtDate(a.session_date), db = b.session_ymd || fmtDate(b.session_date);
  if (da !== db) return da < db ? -1 : 1;
  const ta = a.start_time ? new Date(a.start_time).getTime() : 0, tb = b.start_time ? new Date(b.start_time).getTime() : 0;
  return ta - tb || a.id - b.id;
}

export const authHeaders = (json = true) => {
  const h = { Authorization: 'Bearer ' + localStorage.getItem('token') };
  if (json) h['Content-Type'] = 'application/json';
  return h;
};
